import { MedicareConsent } from '@/consent/medicare/domain/MedicareConsent';
import { MedicareConsentRepository } from '@/consent/medicare/domain/MedicareConsentRepository';
import { ServicesApiResponse, RawServicesError } from '@hfc/ui-core';
import { MedicareConsentApiAdapter } from '@/consent/medicare/infrastructure/adapters/MedicareConsentApiAdapter';
import axios, { AxiosError } from 'axios';
import { UUID } from '@hfc/common/src/types/UUID';

export class MedicareConsentApi implements MedicareConsentRepository {

  public async getMedicareConsent (uuid: UUID): Promise<ServicesApiResponse<MedicareConsent>> {
    try {
      const axiosResponse = await axios.get(`${import.meta.env.VITE_ENROLLMENT_API_URL}/v1/medicare/consent/${uuid}`);

      return {
        success: true,
        data: await MedicareConsentApiAdapter.apiResponseToEntity(axiosResponse.data)
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawServicesError>
      };
    }
  }

  public async submitMedicareConsent (consentForm: MedicareConsent): Promise <ServicesApiResponse> {
    const params = MedicareConsentApiAdapter.entityToApiRequest(consentForm);

    try {
      await axios.post(
        `${import.meta.env.VITE_ENROLLMENT_API_URL}/v1/medicare/consent/${consentForm.id}`,
        params,
        {
          headers: this.getRecaptchaHeaders(consentForm)
        }
      );

      return {
        success: true
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: error as AxiosError<RawServicesError>
      };
    }
  }

  public async updateReferenceId (consentForm: MedicareConsent): Promise <ServicesApiResponse> {
    const params = MedicareConsentApiAdapter.entityToApiRequest(consentForm);

    try {
      await axios.put(
        `${import.meta.env.VITE_ENROLLMENT_API_URL}/v1/medicare/consent/${consentForm.id}/reference-id`,
        params,
        {
          headers: this.getRecaptchaHeaders(consentForm)
        }
      );

      return {
        success: true
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: error as AxiosError<RawServicesError>
      };
    }
  }

  public async saveAndExit (uuid: UUID, email: string|null, phone: number|null, consentForm: MedicareConsent): Promise<ServicesApiResponse<MedicareConsent>> {
    const application = MedicareConsentApiAdapter.entityToSaveAndExitApiRequest(consentForm);

    try {
      const axiosResponse = await axios.post(
        `${import.meta.env.VITE_ENROLLMENT_API_URL}/v1/medicare/consent/${uuid}/save-and-exit`, {
          email: email ? email : undefined,
          phone: phone ? Number(`1${phone}`) : undefined,
          application
        },
        {
          headers: {
            'X-Recaptcha-Token': consentForm.captcha_token
          }
        }
      );

      return {
        success: true,
        data: axiosResponse.data.data
      };
    } catch (error) {
      return {
        success: false,
        error: error as AxiosError<RawServicesError>
      };
    }
  }

  private getRecaptchaHeaders (consentForm: MedicareConsent): { 'X-Recaptcha-Token': string } {
    if (!consentForm.captcha_token) {
      throw new Error('Missing captcha token');
    }

    return {
      'X-Recaptcha-Token': consentForm.captcha_token
    };
  }

}
